import { FC } from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useDirection } from '@hooks';
import { Trans } from '@libs/i18n';
import { ClockIcon } from '@phosphor-icons/react';
import { NoFlipBox } from '@utils';

interface ReassessmentDomainBannerProps {
  parentVersion: number;
  preloadedDocumentCount: number;
  parentDomainScorePercentage: number | null;
}

export const ReassessmentDomainBanner: FC<ReassessmentDomainBannerProps> = observer(
  ({ parentVersion, preloadedDocumentCount, parentDomainScorePercentage }) => {
    const theme = useTheme();
    const { dir } = useDirection();

    return (
      <Paper
        variant="panel"
        elevation={0}
        sx={{
          backgroundColor: 'purple.100',
          border: `1px solid ${theme.palette.purple[200]}`,
          py: 1.25,
          px: 1.75,
        }}
      >
        <NoFlipBox
          mode={dir}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              backgroundColor: 'purple.700',
              width: 28,
              height: 28,
              borderRadius: '8px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ClockIcon color={theme.palette.common.white} size={16} />
          </Box>

          <Typography
            variant="textSM_regular"
            sx={{ color: 'text.primary', flex: 1, minWidth: 0, textAlign: 'start' }}
          >
            <Trans
              i18nKey="app.assessment.workflow.reassessmentDomainBanner.summary"
              values={{
                documentCount: preloadedDocumentCount,
                parentVersion,
              }}
            />
          </Typography>

          {/* Score chip — always rendered in re-assessment context. When the
              parent has no AI results yet, fall back to "pending" copy so the
              user still sees the chip and understands the score is just not
              available, rather than the chip silently disappearing. */}
          <Box
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: 999,
              border: `1px solid ${theme.palette.purple[200]}`,
              backgroundColor: 'common.white',
              color: 'purple.700',
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.5,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              textAlign: 'center',
            }}
          >
            {parentDomainScorePercentage !== null ? (
              <Trans
                i18nKey="app.assessment.workflow.reassessmentDomainBanner.scoreChip"
                values={{
                  parentVersion,
                  percentage: parentDomainScorePercentage,
                }}
              />
            ) : (
              <Trans
                i18nKey="app.assessment.workflow.reassessmentDomainBanner.scoreChipPending"
                values={{ parentVersion }}
              />
            )}
          </Box>
        </NoFlipBox>
      </Paper>
    );
  },
);
